import React, {
    memo,
    useCallback,
    useMemo
} from 'react';
import PropTypes from 'prop-types';

import {
    useGetFocusedInstanceIdValue,
    useCurationConfigValue
} from '../../../hooks/widgetConfiguration.js';

import MetricLoadingView from '../../../components/MetricLoadingView.jsx';

import Empty from './Empty.jsx';
import PanelWithUpsell from './PanelWithUpsell.jsx';

import MetricTableHeader from '../../../components/table/MetricTableHeader.jsx';
import MetricTableCell from '../../../components/table/MetricTableCell.jsx';
import MetricTableProgressBarCell from '../../../components/table/MetricTableProgressBarCell.jsx';
import RIQWordCloud from '../../../components/RIQWordCloud.jsx';
import TableLayoutSelector from './TableLayoutSelector.jsx';
import DriverTypeSelector from './DriverTypeSelector.jsx';
import useContentModal from '../hooks/useContentModal.jsx';

import {
    useRegisterSlideElementsForPPTExport
} from '../../../hooks/useExport.js';

import {
    useSetExportConfig
} from '../../../hooks/exportConfigurationHooks.js';

import useListeningCurationFilterValues from '../hooks/useListeningCurationFilterValues.js';

import {
    listeningDefinitions
} from './enums/listeningDefinitions.jsx';

PositiveNegativeTableWidget.propTypes = {
    instanceId: PropTypes.string.isRequired,
    className: PropTypes.string,
    dimensionTitle: PropTypes.string,
    pdfExportConfig: PropTypes.object,
    appSpecificExportConfig: PropTypes.object
};

function PositiveNegativeTable({dimensionTitle, dimensionName, tableColumnMetricRequestOrder, metricRequests, metricResults, collatedResults, onRowClick}) {

    const metricMaxes = {};
    metricMaxes.counts = collatedResults[0]?.positive ? collatedResults[0].positive[1] : collatedResults[0]?.negative?.[1];
    const formattersByRequestName = _.mapValues(metricRequests, (request) => {
        return {
            abbreviated: RivalIQ.metrics.new.MetricFormatter.getFormatter(request, metricResults.positive, RivalIQ.metrics.new.enums.MetricFormatTypes.abbreviated),
            expanded: RivalIQ.metrics.new.MetricFormatter.getFormatter(request, metricResults.positive, RivalIQ.metrics.new.enums.MetricFormatTypes.expanded)
        };
    });

    const postsDescription = listeningDefinitions.metrics.count;

    let firstHeaderClassName;
    if (_.size(tableColumnMetricRequestOrder) === 1) {
        firstHeaderClassName = 'tlw-w-2/3';
    }

    return (<>
        <table className='tlw-w-full'>
            <thead>
                <tr className='tlw-bg-gray-150'>
                    <MetricTableHeader
                        className={firstHeaderClassName}
                        title={dimensionName}
                    />
                    {_.map(tableColumnMetricRequestOrder, metricRequestName => {

                        return (
                            <MetricTableHeader
                                key={`${dimensionName}-${metricRequestName}`}
                                title={'Posts'}
                                description={postsDescription}
                                alignment={MetricTableHeader.Alignment.RIGHT}
                                sortIndicator={MetricTableHeader.SortIndicator.SORT_DOWN}
                            />
                        );
                    })}
                </tr>
            </thead>

            <tbody>
                {_.map(collatedResults, ({positive, negative}) => {
                    const dimensionValue = positive?.[0] || negative?.[0];
                    const count = positive?.[1] || negative?.[1];
                    const className = positive ? 'tlw-text-green-400' : 'tlw-text-red-400';
                    const DimensionValue = (
                        <span className={className}>
                            {dimensionValue}
                        </span>);
                    return (
                        <tr
                            key={dimensionValue + className}
                            className={`tlw-h-[47px] tlw-cursor-pointer tlw-border-0 tlw-border-t tlw-border-solid tlw-border-gray-200 hover_tlw-bg-blue-50`}
                            onClick={() => onRowClick(dimensionValue, Boolean(positive), count)}>
                            <MetricTableCell
                                abbreviatedValue={DimensionValue}
                                metricName={`${dimensionTitle}`}
                                expandedValue={dimensionValue}
                                alignment={MetricTableCell.Alignment.LEFT}
                                className={`tlw-w-full tlw-truncate tlw-text-left`}
                            />

                            <MetricTableProgressBarCell
                                key={dimensionValue + count}
                                rawValue={count}
                                maxValue={metricMaxes.counts}
                                abbreviatedValue={formattersByRequestName.positive.abbreviated(count)}
                                metricName={'Posts'}
                                expandedValue={formattersByRequestName.positive.expanded(count)}
                                valueClassName='tlw-w-[50px] tlw-text-right'
                            />

                        </tr>
                    );
                })}
            </tbody>
        </table>
    </>);
}

const PositiveNegativeWordCloud = memo(function PositiveNegativeWordCloud({collatedResults, onWordClick}) {
    const words = useMemo(() => {
        return _.map(collatedResults, (data) => {
            const counts = data.positive || data.negative;
            const isPositive = Boolean(data.positive);
            const fillColorClass = isPositive ? 'tlw-fill-green-400' : 'tlw-fill-red-400';
            return {
                text: counts[0],
                value: counts[1],
                isPositive: isPositive,
                className: `${fillColorClass} hover_tlw-opacity-60`
            };
        });
    }, [collatedResults]);

    return (
        <RIQWordCloud
            words={words}
            onWordClick={onWordClick}
        />
    );
});

const getCollatedResultsFromMetricResults = _.memoize((metricResults, rowLimit) => {
    const intermediateCollatedResults = RivalIQ.metrics.new.MetricResponse.groupResponsesByFixedDimensions(metricResults).sort((a, b) => {
        let {positive, negative} = a;
        const countA = positive?.[1] || negative?.[1];
        const valueA = positive?.[0] || negative?.[0];
        ({positive, negative} = b);
        const countB = positive?.[1] || negative?.[1];
        const valueB = positive?.[0] || negative?.[0];
        return countB - countA || valueA.localeCompare(valueB);
    });
    return (rowLimit > 0 ? _.take(intermediateCollatedResults, rowLimit) : intermediateCollatedResults);
});

export default function PositiveNegativeTableWidget({instanceId, className, dimensionTitle, pdfExportConfig, appSpecificExportConfig}) {

    const {
        title = 'Sentiment',
        description,
        footer: footerContextView
    } = listeningDefinitions[instanceId] || {};

    const layout = useCurationConfigValue(instanceId, 'layout');
    const driver = useCurationConfigValue(instanceId, 'driver');

    let positiveDimension;
    let negativeDimension;
    let dimensionName;

    switch (driver) {
        case 'behaviors': {
            positiveDimension = 'positiveBehavior';
            negativeDimension = 'negativeBehavior';
            dimensionName = 'Behaviors';
            break;
        }
        case 'emotions': {
            positiveDimension = 'positiveEmotion';
            negativeDimension = 'negativeEmotion';
            dimensionName = 'Emotions';
            break;
        }
        case 'attributes':
        default: {
            positiveDimension = 'like';
            negativeDimension = 'dislike';
            dimensionName = 'Attributes';
            break;
        }
    }

    dimensionTitle = dimensionTitle || title || dimensionName;

    const metricFilters = useListeningCurationFilterValues(instanceId);
    useRegisterSlideElementsForPPTExport(instanceId, {
        header: {
            title,
            subtitle: appSpecificExportConfig?.timePeriodStr
        }
    });

    const focusedInstanceId = useGetFocusedInstanceIdValue();

    const {showContentModal, ContentModal} = useContentModal(`${driver}-${instanceId}`);

    const summaryLimit = (layout === 'wordcloud' ? 50 : 8);
    const viewMoreLimit = (layout === 'wordcloud' ? 100 : -1);
    const rowLimit = (focusedInstanceId === instanceId ? viewMoreLimit : summaryLimit);
    metricFilters.listening.limit = rowLimit;
    const metricRequests = {
        positive: {
            dataSourceId: RivalIQ.metrics.new.enums.DataSourceIds.LISTENING_QUERY_SEARCH,
            dimensions: [positiveDimension],
            measure: 'count',
            filters: metricFilters
        },
        negative: {
            dataSourceId: RivalIQ.metrics.new.enums.DataSourceIds.LISTENING_QUERY_SEARCH,
            dimensions: [negativeDimension],
            measure: 'count',
            filters: metricFilters
        }
    };
    // This is an array of keys into the metricRequests object above to control the order of the columns in the table.
    const tableColumnMetricRequestOrder = [ 'counts'];

    const dimensions = {
        positive: positiveDimension,
        negative: negativeDimension
    };
    useSetExportConfig(instanceId, {
        title: `Sentiment Drivers by ${dimensionName}`,
        supportedExportFormats: _.values(RivalIQ.enums.exportFormat),
        supportedExportTypes: _.values(RivalIQ.enums.exportType),
        csvExportConfig: {
            type: 'dataSourceMetricsGenerator',
            headerNameOverrides: [dimensionName, 'posts', 'sentiment'],
            sortByColumns: [[1, 0], ['desc', 'asc']],
            metricGroups: _.map(dimensions, (dimension, sentiment) => {
                const baseMetric = metricRequests[sentiment];
                const dimensionValueMetric = JSON.parse(JSON.stringify(_.defaults({
                    measure: {
                        field: baseMetric.measure,
                        returns: `dimensionValue(${dimension})`
                    }
                }, baseMetric)));
                dimensionValueMetric.filters.listening.limit = -1;
                return [dimensionValueMetric, baseMetric, {
                    constant: dimensionName === 'Attributes' ? dimension : sentiment
                }];
            })
        },
        pdfExportConfig,
        appSpecificExportConfig
    });

    const onRowClick = useCallback((dimensionValue, isPositive, count) => {
        const overrides = {
            [positiveDimension]: null,
            [negativeDimension]: null
        };

        const filterKey = isPositive ? positiveDimension : negativeDimension;
        overrides[filterKey] = dimensionValue;
        showContentModal(overrides);
    }, [negativeDimension, positiveDimension, showContentModal]);

    const onWordClick = useCallback((word) => {
        onRowClick(word.text, word.isPositive, word.value);
    }, [onRowClick]);

    return (
        <PanelWithUpsell
            instanceId={instanceId}
            title={`${dimensionTitle}`}
            description={description}
            exportSubtitle={appSpecificExportConfig?.timePeriodStr}
            showViewMore={true}
            className={className}
            additionalTitleBarControls={<>
                <DriverTypeSelector instanceId={instanceId} />
                <TableLayoutSelector instanceId={instanceId} />
            </>}
            isDescriptionHtml={false}
            footerContextView={footerContextView}
        >
            <MetricLoadingView
                emptyFallback={<Empty subject={_.lowerCase(dimensionName)}/>}
                instanceId={instanceId}
                metricRequests={metricRequests}>
                {
                    (metricResults) => {
                        const collatedResults = getCollatedResultsFromMetricResults(metricResults, rowLimit);
                        if (layout === 'wordcloud') {
                            return (
                                <PositiveNegativeWordCloud
                                    collatedResults={collatedResults}
                                    onWordClick={onWordClick}
                                />
                            );
                        }

                        return (
                            <PositiveNegativeTable
                                dimensionTitle={dimensionTitle}
                                dimensionName={dimensionName}
                                tableColumnMetricRequestOrder={tableColumnMetricRequestOrder}
                                metricRequests={metricRequests}
                                metricResults={metricResults}
                                collatedResults={collatedResults}
                                onRowClick={onRowClick}
                            />
                        );
                    }
                }
            </MetricLoadingView>

            {ContentModal}
        </PanelWithUpsell>
    );
}
